import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, ListChecks, BookOpenCheck, BarChart3, Activity, LineChart,
  Sparkles, Target, Users, Lightbulb, Mail, Inbox, LogIn,
  CheckCircle2, ArrowRight, Quote, GraduationCap, Stethoscope, RotateCcw, School,
  FileText, BrainCircuit, Award, Bell, Trophy,
  Tag, AlarmClock, Zap, BadgePercent, Flame, Rocket, Copy, ShieldCheck, GiftIcon
} from "lucide-react";

// ───────────────────────────── CONFIG ─────────────────────────────
// Replace this to change all buy CTAs (payment / checkout link)
const ACCESS_LINK = "https://plancourr.mojo.page/arrnode-prep";
// Launch offer ends 15 days from the user's first visit (stored in localStorage)
// so the urgency banner is always live for every new visitor.
const LAUNCH_WINDOW_DAYS = 15;
const getLaunchEnd = () => {
  if (typeof window === "undefined") return new Date(Date.now() + LAUNCH_WINDOW_DAYS * 86400000);
  const stored = window.localStorage.getItem("arrnode-launch-end");
  if (stored) {
    const d = new Date(stored);
    // If somehow corrupted or already past, reset
    if (!isNaN(d.getTime()) && d.getTime() > Date.now()) return d;
  }
  const fresh = new Date(Date.now() + LAUNCH_WINDOW_DAYS * 86400000);
  window.localStorage.setItem("arrnode-launch-end", fresh.toISOString());
  return fresh;
};
const LAUNCH_END = getLaunchEnd();
const COUPON = "ARRNODE2026";
const ORIGINAL_PRICE = 999;
const REGULAR_PRICE = 399;
const LAUNCH_PRICE = 299;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" }
};

// ───────────────────────────── COUNTDOWN ─────────────────────────────
const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, finished: diff === 0 };
};

const PromoBanner = () => {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_END);
  return (
    <div className="relative w-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 text-white text-xs sm:text-sm overflow-hidden" data-testid="promo-banner">
      <div className="absolute inset-0 opacity-25 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-medium">
        <span className="inline-flex items-center gap-1.5">
          <Flame className="h-3.5 w-3.5" />
          <strong>Launch offer</strong> — ₹{LAUNCH_PRICE} only
        </span>
        <span className="hidden sm:inline opacity-70">•</span>
        <span className="inline-flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5" /> Coupon <span className="font-mono font-bold tracking-wider bg-white/15 px-1.5 py-0.5 rounded">{COUPON}</span>
        </span>
        <span className="hidden sm:inline opacity-70">•</span>
        <span className="inline-flex items-center gap-1.5 font-mono tabular-nums">
          <AlarmClock className="h-3.5 w-3.5" />
          {String(days).padStart(2, "0")}d : {String(hours).padStart(2, "0")}h : {String(minutes).padStart(2, "0")}m : {String(seconds).padStart(2, "0")}s left
        </span>
        <a href={ACCESS_LINK} target="_blank" rel="noreferrer" data-testid="promo-banner-cta" className="ml-2 inline-flex items-center gap-1 rounded-full bg-white text-fuchsia-700 px-3 py-1 text-[11px] font-bold hover:bg-fuchsia-50 transition-colors">
          Grab it now <ArrowRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
};

// Reusable mid-page hook strip
const BuyHook = ({ id = "" }) => (
  <section className="py-10 md:py-14">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.a
        href={ACCESS_LINK}
        target="_blank"
        rel="noreferrer"
        data-testid={`buy-hook-strip${id ? "-" + id : ""}`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="group relative block rounded-2xl sm:rounded-3xl overflow-hidden border border-violet-200/60 dark:border-violet-500/30 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 text-white p-5 sm:p-8 hover:-translate-y-1 hover:shadow-2xl hover:shadow-fuchsia-500/30 transition-all duration-500"
      >
        <div className="absolute inset-0 opacity-30 grain pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Rocket className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest font-bold text-white/80">Launching Offer • Only ₹{LAUNCH_PRICE}</p>
              <h3 className="font-heading text-xl sm:text-2xl font-bold mt-1 leading-snug">
                Your personalised Productivity  JEE / NEET dashboard for <span className="line-through opacity-60 mr-1">₹{ORIGINAL_PRICE}</span> <span className="line-through opacity-60 mr-1">₹{REGULAR_PRICE}</span> <span className="text-yellow-200">₹{LAUNCH_PRICE}</span> only
              </h3>
              <p className="text-sm text-white/85 mt-1.5">A productivity system that walks along your journey — less than ₹4 a day, full 3 months access.</p>
            </div>
          </div>
          <span className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-fuchsia-700 px-5 h-11 font-bold text-sm self-stretch sm:self-start md:self-center whitespace-nowrap shadow-lg group-hover:scale-105 transition-transform">
            Grab it now <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </motion.a>
    </div>
  </section>
);

// ---------- HERO ----------
const Hero = () => (
  <section id="home" className="relative pt-16 pb-24 md:pt-24 md:pb-32 overflow-hidden">
    <div className="absolute inset-0 -z-10 hero-aurora" />
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div {...fadeUp} className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-zinc-800 bg-white/70 dark:bg-zinc-900/60 backdrop-blur px-3 py-1 mb-6">
            <Sparkles className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            <span className="text-xs font-medium tracking-wide text-slate-700 dark:text-zinc-300">For JEE & NEET Aspirants</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.05]">
            Plan Smarter.<br />
            Track Better.<br />
            <span className="gradient-text">Prepare Stronger.</span>
          </h1>

          <div className="mt-6 rounded-2xl border-l-4 border-fuchsia-500 bg-fuchsia-50/60 dark:bg-fuchsia-500/5 pl-5 pr-4 py-3.5 max-w-2xl">
            <p className="font-heading text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-snug">
              Your personalised <span className="gradient-text">JEE / NEET</span> productivity dashboard.
            </p>
            <p className="text-sm sm:text-base text-slate-700 dark:text-zinc-300 mt-1 leading-relaxed">
              Built to <span className="font-semibold text-fuchsia-700 dark:text-fuchsia-400">walk along your journey</span> — from first chapter to final mock, planning every day, tracking every mistake, and pushing your rank higher with every consistent step.
            </p>
          </div>

          <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-zinc-400 max-w-2xl leading-relaxed">
            Arrnode Prep is a productivity dashboard for JEE and NEET students that helps you manage daily study plans, weekly goals, chapter progress, mock test analysis, and habits — all in one place.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
            <a href={ACCESS_LINK} target="_blank" rel="noreferrer" data-testid="hero-get-dashboard-btn" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto rounded-full min-h-12 h-auto px-5 sm:px-6 py-3 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 hover:from-violet-700 hover:via-fuchsia-700 hover:to-rose-600 text-white text-sm font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 hover:scale-[1.02] transition-all duration-300 whitespace-normal">
                <Rocket className="h-4 w-4 mr-2" /> Get My Personalised Dashboard
              </Button>
            </a>
            <Link to="/login" data-testid="hero-login-btn" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto rounded-full min-h-12 h-auto px-5 sm:px-6 py-3 border-slate-300 dark:border-zinc-700 text-sm font-medium whitespace-normal">
                <LogIn className="h-4 w-4 mr-2" /> Already paid? Login
              </Button>
            </Link>
          </div>

          <div className="mt-5 inline-flex items-center gap-2 text-xs text-slate-600 dark:text-zinc-400" data-testid="hero-offer-pill">
            <BadgePercent className="h-3.5 w-3.5 text-fuchsia-600 dark:text-fuchsia-400" />
            <span>
              <span className="line-through text-slate-400 dark:text-zinc-600">₹{ORIGINAL_PRICE}</span>
              <span className="line-through text-slate-400 dark:text-zinc-600 ml-1.5">₹{REGULAR_PRICE}</span>
              <span className="font-bold text-slate-900 dark:text-white ml-1.5">₹{LAUNCH_PRICE}</span>
              <span className="ml-1.5">• Launch offer • Less than ₹4 / day</span>
            </span>
          </div>

          <div className="mt-10 flex flex-wrap gap-6 sm:gap-10">
            {[
              { v: "10+", l: "Study Tools" },
              { v: "100%", l: "Personalized" },
              { v: "24/7", l: "Access" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-heading text-3xl font-bold text-slate-900 dark:text-white">{s.v}</div>
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 dark:text-zinc-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -inset-8 -z-10 bg-gradient-to-br from-indigo-500/15 via-violet-500/10 to-blue-500/15 blur-3xl rounded-full" />
          <div className="relative h-[420px] sm:h-[500px] lg:h-[560px]">
            <motion.div
              initial={{ opacity: 0, x: 28, y: -16, rotate: 2 }}
              whileInView={{ opacity: 1, x: 0, y: 0, rotate: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.75, ease: "easeOut", delay: 0.2 }}
              className="absolute right-0 top-0 z-10 w-[88%] overflow-hidden rounded-3xl border border-indigo-200/80 bg-white p-2 shadow-2xl shadow-slate-900/10 dark:border-indigo-500/30 dark:bg-zinc-950 dark:shadow-black/40"
            >
              <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-white dark:bg-zinc-950">
                <img
                  src="/dashboard-previews/chemistry-roadmap.png"
                  alt="Chemistry study roadmap preview"
                  decoding="async"
                  fetchPriority="high"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -28, y: 28, rotate: -2 }}
              whileInView={{ opacity: 1, x: 0, y: 0, rotate: -1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.75, ease: "easeOut", delay: 0.32 }}
              className="absolute bottom-0 left-0 z-20 w-[82%] overflow-hidden rounded-3xl border border-violet-200/80 bg-white p-2 shadow-2xl shadow-slate-900/15 dark:border-violet-500/30 dark:bg-zinc-950 dark:shadow-black/50"
            >
              <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-white dark:bg-zinc-950">
                <img
                  src="/dashboard-previews/overall-breakdown.png"
                  alt="Arrnode mock test analysis preview"
                  decoding="async"
                  loading="lazy"
                  className="h-full w-full object-cover object-center scale-110"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// ---------- WHO IS IT FOR ----------
const audience = [
  { icon: GraduationCap, title: "JEE Aspirants", desc: "Engineering dreamers preparing for IIT-JEE Main & Advanced" },
  { icon: Stethoscope, title: "NEET Aspirants", desc: "Future doctors preparing for medical entrance exams" },
  { icon: RotateCcw, title: "Drop Year Students", desc: "Students taking a gap year for focused preparation" },
  { icon: School, title: "Class 11 & 12 Students", desc: "Early starters building strong foundations" },
];

const WhoForSection = () => (
  <section className="py-20 md:py-28 border-y border-slate-200/70 dark:border-zinc-800/60 bg-slate-50/50 dark:bg-zinc-900/30">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className="text-xs uppercase tracking-widest font-semibold text-indigo-600 dark:text-indigo-400 mb-3">Who is Arrnode Prep for?</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
          Built for students who are serious about cracking competitive exams
        </h2>
      </motion.div>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {audience.map((a, i) => (
          <motion.div
            key={a.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group relative rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-6 hover:border-indigo-300 dark:hover:border-indigo-500/60 hover:-translate-y-1 transition-all duration-300"
            data-testid={`audience-card-${a.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
          >
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/10 dark:to-violet-500/10 flex items-center justify-center mb-4">
              <a.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">{a.title}</h3>
            <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1.5 leading-relaxed">{a.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- PROBLEM ----------
const problems = [
  { title: "No clear daily study structure", desc: "Students start the day with no plan and end with regret." },
  { title: "Forgetting weak chapters", desc: "Topics get skipped, mistakes repeat, and gaps grow silently." },
  { title: "Mock tests without proper analysis", desc: "Taking mocks but never understanding what actually went wrong." },
  { title: "Inconsistent habits and revision", desc: "A great week followed by three lost ones — no real progress." },
];

const ProblemSection = () => (
  <section className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className="text-xs uppercase tracking-widest font-semibold text-rose-600 dark:text-rose-400 mb-3">The Hard Truth</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
          Most students don't fail because they don't study.<br />
          <span className="text-slate-500 dark:text-zinc-500">They fail because they don't track.</span>
        </h2>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {problems.map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-6"
            data-testid={`problem-card-${i}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-xs text-rose-500">0{i + 1}</span>
              <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
            </div>
            <h3 className="font-heading text-base font-semibold text-slate-900 dark:text-white">{p.title}</h3>
            <p className="text-sm text-slate-600 dark:text-zinc-400 mt-2 leading-relaxed">{p.desc}</p>
          </motion.div>
        ))}
      </div>

      <motion.div {...fadeUp} className="mt-12 rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-blue-600 p-8 sm:p-10">
        <p className="font-heading text-xl sm:text-2xl font-semibold text-white leading-snug max-w-3xl">
          Arrnode Prep gives students a simple system to plan, track, analyze, and improve their preparation every day.
        </p>
      </motion.div>
    </div>
  </section>
);

// ---------- FEATURES (Bento) ----------
const FeaturesSection = () => {
  const features = [
    { icon: Calendar, title: "Daily Planner", desc: "Plan your study day with clear tasks, subjects, and priorities.", span: "md:col-span-4 lg:col-span-5" },
    { icon: ListChecks, title: "Weekly Planner", desc: "Set weekly goals and stay aligned with your preparation timeline.", span: "md:col-span-4 lg:col-span-3" },
    { icon: BookOpenCheck, title: "Chapterwise Tracker", desc: "Track completed, pending, weak, and revision-needed chapters.", span: "md:col-span-4 lg:col-span-4" },
    { icon: BarChart3, title: "Self Mock Test Analysis", desc: "Analyze your mock test performance and identify weak areas.", span: "md:col-span-4 lg:col-span-4" },
    { icon: Activity, title: "Habit Tracker", desc: "Build consistency through daily study, revision, and test habits.", span: "md:col-span-4 lg:col-span-4" },
    { icon: LineChart, title: "Progress Dashboard", desc: "See your preparation progress in one clean dashboard.", span: "md:col-span-4 lg:col-span-4" },
    { icon: Target, title: "Productivity System", desc: "Stay focused without unnecessary distractions.", span: "md:col-span-4 lg:col-span-4" },
    { icon: Award, title: "Exam-Focused Workflow", desc: "Designed specifically for JEE and NEET aspirants.", span: "md:col-span-4 lg:col-span-4" },
    { icon: Lightbulb, title: "Community Learning", desc: "Connect with fellow aspirants and share strategies.", span: "md:col-span-4 lg:col-span-4", soon: true },
  ];

  return (
    <section id="features" className="py-20 md:py-28 border-t border-slate-200/70 dark:border-zinc-800/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <motion.div {...fadeUp} className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-widest font-semibold text-indigo-600 dark:text-indigo-400 mb-3">Features</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            Everything a serious JEE/NEET student needs to stay organized
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className={`group relative rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-6 sm:p-7 hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${f.span}`}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {f.soon && (
                <span className="absolute top-5 right-5 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5">
                  Soon
                </span>
              )}
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/10 dark:to-violet-500/10 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <f.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">{f.title}</h3>
              <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1.5 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- DASHBOARD GLIMPSE ----------
const previewImages = [
  { src: "/dashboard-previews/study-roadmap.png", alt: "Study roadmap tracker", wide: true },
  { src: "/dashboard-previews/chapter-detail.png", alt: "Chapter detail tracker", wide: true },
  { src: "/dashboard-previews/overall-breakdown.png", alt: "Overall mock test analysis" },
  { src: "/dashboard-previews/physics-breakdown.png", alt: "Physics mock test analysis" },
  { src: "/dashboard-previews/analytics-charts.png", alt: "Dashboard charts and question distribution" },
];

const PreviewSection = () => (
  <section className="py-20 md:py-28 bg-slate-50/50 dark:bg-zinc-900/30 border-y border-slate-200/70 dark:border-zinc-800/60">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.div {...fadeUp} className="mb-10">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
          Dashboard Glimpse
        </h2>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-2">
        {previewImages.map((image, index) => (
          <motion.div
            key={image.src}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            className={`glass rounded-3xl p-3 sm:p-4 shadow-xl shadow-slate-900/10 dark:shadow-black/40 ${image.wide ? "lg:col-span-2" : ""}`}
          >
            <div className={`relative overflow-hidden rounded-2xl bg-white dark:bg-zinc-950 ${image.wide ? "aspect-[21/9]" : "aspect-[16/9]"}`}>
              <img
                src={image.src}
                alt={image.alt}
                className={`h-full w-full ${image.wide ? "object-cover object-top" : "object-contain"}`}
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: previewImages.length * 0.08 }}
          className="glass rounded-3xl p-8 sm:p-10 min-h-[280px] flex items-center justify-center text-center shadow-xl shadow-slate-900/10 dark:shadow-black/40"
        >
          <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-tight">
            AND MANY MORE FEATURES
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);
// ---------- TESTIMONIALS ----------
const testimonials = [
  { name: "Rahul Sharma", tag: "JEE Aspirant", quote: "Arrnode Prep changed how I study. I finally understand my weak areas and can track my progress systematically." },
  { name: "Priya Patel", tag: "NEET Aspirant", quote: "My mock test analysis improved my score drastically. The detailed breakdown helped me focus on what matters." },
  { name: "Arjun Mehta", tag: "JEE Aspirant", quote: "The planner keeps me consistent. After a gap year, I needed structure and Arrnode Prep gave me exactly that." },
  { name: "Sneha Gupta", tag: "NEET Aspirant", quote: "Started early with Arrnode Prep and it made balancing boards and JEE prep so much easier!" },
];

const TestimonialsSection = () => (
  <section className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.div {...fadeUp} className="max-w-3xl mb-12">
        <p className="text-xs uppercase tracking-widest font-semibold text-indigo-600 dark:text-indigo-400 mb-3">Testimonials</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
          Loved by aspirants
        </h2>
        <p className="mt-4 text-slate-600 dark:text-zinc-400">See what students are saying about their experience.</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {testimonials.map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-7 hover:-translate-y-1 hover:shadow-md transition-all"
            data-testid={`testimonial-${i}`}
          >
            <Quote className="h-6 w-6 text-indigo-500 mb-4" />
            <p className="text-slate-700 dark:text-zinc-300 leading-relaxed">"{t.quote}"</p>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center font-heading font-bold text-white text-sm">
                {t.name.split(" ").map((p) => p[0]).join("")}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{t.name}</p>
                <p className="text-xs text-slate-500 dark:text-zinc-500">{t.tag}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- HOW IT WORKS ----------
const steps = [
  { icon: ListChecks, title: "Take Access", desc: "After taking the plan, your registered email will be authorized for student portal access." },
  { icon: Inbox, title: "Check Your Mail", desc: "You will receive your personalized Arrnode Prep website link and login details on your registered email." },
  { icon: LogIn, title: "Login and Start Planning", desc: "Use your authorized email to log in and follow the instructions inside the Student Portal." },
];

const HowSection = () => (
  <section id="how" className="py-20 md:py-28 border-y border-slate-200/70 dark:border-zinc-800/60 bg-slate-50/50 dark:bg-zinc-900/30">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.div {...fadeUp} className="max-w-3xl mb-12">
        <p className="text-xs uppercase tracking-widest font-semibold text-indigo-600 dark:text-indigo-400 mb-3">How it works</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
          How Arrnode Prep access works
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {steps.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-7"
            data-testid={`step-card-${i + 1}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
                <s.icon className="h-5 w-5 text-white" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-slate-400">Step {i + 1}</span>
            </div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">{s.title}</h3>
            <p className="text-sm text-slate-600 dark:text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>

      <motion.div {...fadeUp} className="mt-8 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/5 p-5 flex gap-3" data-testid="spam-note-howitworks">
        <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
          <span className="font-semibold">Already taken our plan?</span> Please check your email inbox. If you don't see an email from us within a few hours, check your spam/junk folder just in case it landed there.
        </p>
      </motion.div>
    </div>
  </section>
);

// ---------- BUY SECTION (Single Plan + Launch Offer) ----------
const planFeatures = [
  "Personal Study Dashboard",
  "Self Mock Test Analysis System",
  "Chapter Progress Tracker",
  "Daily & Weekly Study Planner",
  "Real Exam Test Environment",
  "Pomodoro Focus Timer",
  "Voice to Text Notes",
  "Printable Study Materials",
  "Complete Study Roadmap",
];

const CountdownPill = ({ label, value }) => (
  <div className="flex flex-col items-center min-w-[44px] sm:min-w-[58px]">
    <div className="font-heading text-xl sm:text-3xl font-bold tabular-nums text-white">{String(value).padStart(2, "0")}</div>
    <div className="text-[10px] uppercase tracking-widest text-white/70 mt-0.5">{label}</div>
  </div>
);

const BuySection = () => {
  const { days, hours, minutes, seconds } = useCountdown(LAUNCH_END);
  const [copied, setCopied] = useState(false);

  const copyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(COUPON);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <section id="buy" className="py-20 md:py-28 relative overflow-hidden">
      {/* decorative blobs */}
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-fuchsia-400/10 blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 relative">
        <motion.div {...fadeUp} className="text-center mb-10">
          <p className="inline-flex items-center gap-2 rounded-full bg-fuchsia-100 dark:bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 text-xs uppercase tracking-widest font-bold px-4 py-1.5 mb-4">
            <Rocket className="h-3.5 w-3.5" /> Launching Offer • Only ₹{LAUNCH_PRICE}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
            Get your <span className="gradient-text">personalised dashboard</span>
          </h2>
          <p className="mt-4 text-slate-600 dark:text-zinc-400 max-w-2xl mx-auto">
            One simple plan. A personalised JEE/NEET productivity dashboard built to walk along your journey — every day, every chapter, every mock.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          data-testid="buy-plan-card"
          className="relative rounded-[2rem] overflow-hidden"
        >
          {/* animated gradient border */}
          <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 opacity-90" />
          <div className="absolute inset-[2px] rounded-[1.95rem] bg-white dark:bg-zinc-950" />

          <div className="relative p-7 sm:p-10">
            {/* badge */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 shadow-lg shadow-fuchsia-500/30">
                <Flame className="h-3 w-3" /> Most Popular • Launch Special
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[11px] font-semibold px-3 py-1.5 border border-amber-200 dark:border-amber-500/30">
                <AlarmClock className="h-3 w-3" /> Last 15 days only
              </span>
            </div>

            <h3 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white text-center">
              ARRNODE PREP
            </h3>
            <p className="text-center text-slate-500 dark:text-zinc-500 mt-1.5 text-sm">Complete Preparation System • 3 Months Access</p>

            {/* Price block */}
            <div className="mt-7 flex flex-col items-center" data-testid="plan-price-block">
              <div className="flex items-baseline gap-3">
                <span className="text-lg sm:text-xl text-slate-400 dark:text-zinc-600 line-through">₹{ORIGINAL_PRICE}</span>
                <span className="text-lg sm:text-xl text-slate-400 dark:text-zinc-600 line-through decoration-rose-500">₹{REGULAR_PRICE}</span>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-6xl sm:text-7xl font-bold gradient-text leading-none">₹{LAUNCH_PRICE}</span>
                <span className="text-sm text-slate-500 dark:text-zinc-500">/ 3 months</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs">
                <span className="rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-3 py-1 font-semibold border border-emerald-200 dark:border-emerald-500/30">
                  Less than ₹4 / day
                </span>
              </div>
            </div>

            {/* Coupon */}
            <button
              onClick={copyCoupon}
              data-testid="copy-coupon-btn"
              className="mt-7 w-full group relative rounded-2xl border-2 border-dashed border-fuchsia-300 dark:border-fuchsia-500/40 bg-fuchsia-50/40 dark:bg-fuchsia-500/5 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-500/10 transition-colors p-4 flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                  <GiftIcon className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-600 dark:text-fuchsia-400">Apply Coupon</p>
                  <p className="font-mono text-base sm:text-lg font-bold text-slate-900 dark:text-white tracking-widest">{COUPON}</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-fuchsia-600 dark:text-fuchsia-400">
                <Copy className="h-3.5 w-3.5" /> {copied ? "Copied!" : "Tap to copy"}
              </span>
            </button>

            {/* Countdown */}
            <div className="mt-6 rounded-2xl bg-slate-900 dark:bg-zinc-900 p-5" data-testid="countdown-timer">
              <p className="text-center text-[10px] uppercase tracking-widest font-semibold text-white/70 mb-3">⏳ Launch offer ends in</p>
              <div className="flex justify-center items-center gap-1.5 sm:gap-5">
                <CountdownPill label="Days" value={days} />
                <span className="text-white/40 font-heading text-xl sm:text-2xl">:</span>
                <CountdownPill label="Hours" value={hours} />
                <span className="text-white/40 font-heading text-xl sm:text-2xl">:</span>
                <CountdownPill label="Mins" value={minutes} />
                <span className="text-white/40 font-heading text-xl sm:text-2xl">:</span>
                <CountdownPill label="Secs" value={seconds} />
              </div>
            </div>

            {/* Features */}
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {planFeatures.map((f, i) => (
                <motion.li
                  key={f}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-zinc-300"
                >
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-fuchsia-500" />
                  <span>{f}</span>
                </motion.li>
              ))}
            </ul>

            {/* Main CTA */}
            <a href={ACCESS_LINK} target="_blank" rel="noreferrer" className="block mt-9">
              <Button
                data-testid="buy-cta-btn"
                className="w-full min-h-14 h-auto rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 hover:from-violet-700 hover:via-fuchsia-700 hover:to-rose-600 text-white font-bold text-sm sm:text-base shadow-xl shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 hover:scale-[1.01] transition-all duration-300 whitespace-normal py-3 px-4"
              >
                <Rocket className="h-5 w-5 mr-2" /> Grab it now — ₹{LAUNCH_PRICE} only
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </a>

            <div className="mt-5 flex flex-wrap justify-center gap-4 text-[11px] text-slate-500 dark:text-zinc-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Instant access</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-emerald-500" /> Mobile + Desktop</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Secure checkout</span>
            </div>
          </div>
        </motion.div>

        <p className="mt-6 text-center text-xs text-slate-500 dark:text-zinc-500">
          🔒 100% safe & secure • After purchase, you'll receive your personalised dashboard link on your email within minutes.
        </p>
      </div>
    </section>
  );
};

// ---------- FAQ ----------
const faqs = [
  { q: "What is ARRNODE PREP?", a: "ARRNODE PREP is a smart productivity tool platform for JEE and NEET aspirants. It helps students plan their study day, track chapters, analyze mock tests, build habits, and stay consistent throughout their preparation." },
  { q: "Is ARRNODE PREP a coaching or lecture platform?", a: "No. ARRNODE PREP does not provide video lectures or study content. It gives you a structured system to manage your preparation better through planning, tracking, analysis, and consistency tools." },
  { q: "How do I access ARRNODE PREP after purchase?", a: "After purchasing ARRNODE PREP, you will receive your access details and personalized dashboard website link on your registered email. Open that link, follow the instructions, and start using your personalized JEE/NEET preparation dashboard. Sometimes emails may go to spam or promotions, so please check inbox, spam, and promotions folders. If you still do not find it, contact us at arrnodeprep@gmail.com." },
  { q: "What if I do not receive my access email?", a: "Please check your spam, promotions, and updates folders first. Sometimes important emails may go there. If you still cannot find your access mail, contact ARRNODE PREP support with your registered email ID and purchase details at arrnodeprep@gmail.com." },
  { q: "Is ARRNODE PREP suitable for NEET preparation?", a: "Yes. ARRNODE PREP is designed for both JEE and NEET aspirants. NEET students can use it to plan daily tasks, track Biology, Physics, and Chemistry chapters, analyze mock tests, and improve consistency." },
  { q: "Is ARRNODE PREP suitable for JEE preparation?", a: "Yes. JEE aspirants can use ARRNODE PREP to organize Physics, Chemistry, and Mathematics preparation, track chapter completion, analyze mock tests, and follow a more disciplined study routine." },
  { q: "Does it work on mobile devices?", a: "Yes. ARRNODE PREP works on mobile, tablet, laptop, and desktop. You can also add your personalized dashboard to your mobile home screen and use it like an app." },
  { q: "Can I install ARRNODE PREP like an app?", a: "Yes. After opening your personalized dashboard link, you can add it to your mobile home screen or install it from your laptop browser using the browser PWA install option. This lets you use ARRNODE PREP like a normal app without downloading anything from the Play Store or App Store." },
  { q: "How do I track my mock tests?", a: "You can enter your mock test details, marks, subject-wise performance, weak areas, and improvement notes. ARRNODE PREP helps you understand where you are losing marks and what you should focus on next." },
  { q: "Can I compare multiple mock tests?", a: "Yes. ARRNODE PREP helps you review your mock test journey and compare your performance over time so you can identify whether you are improving, stuck, or repeating the same mistakes." },
  { q: "Who can access the Student Portal?", a: "Only authorized students can access the ARRNODE PREP Student Portal. There is no public signup option. After purchase, your registered email is authorized and access details are sent to your mail." },
  { q: "What happens after I purchase?", a: "After purchase, you will get your access details and personalized ARRNODE PREP dashboard website link on your registered email. From there, you can open your dashboard, save it like an app, and start managing your JEE/NEET journey. If the mail is not visible, please check your spam or promotions section." },
  { q: "Can I use ARRNODE PREP without buying access?", a: "No. ARRNODE PREP is available only for authorized students. You can explore the landing page, but dashboard access is given only after purchase." },
  { q: "What if I face any issue while logging in?", a: "If you are unable to log in, first make sure you are using the same email that was used during purchase. Also check your access mail carefully. If the issue still continues, contact ARRNODE PREP support at arrnodeprep@gmail.com." },
];

const FAQSection = () => (
  <section id="faq" className="py-20 md:py-28 border-y border-slate-200/70 dark:border-zinc-800/60 bg-slate-50/50 dark:bg-zinc-900/30">
    <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12">
      <motion.div {...fadeUp} className="text-center mb-10">
        <p className="text-xs uppercase tracking-widest font-semibold text-indigo-600 dark:text-indigo-400 mb-3">FAQ</p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
          Frequently asked questions
        </h2>
        <p className="mt-4 text-slate-600 dark:text-zinc-400">Everything you need to know about Arrnode Prep.</p>
      </motion.div>

      <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
        {faqs.map((f, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-5 [&[data-state=open]]:border-indigo-300 dark:[&[data-state=open]]:border-indigo-500/50"
          >
            <AccordionTrigger
              data-testid={`faq-trigger-${i}`}
              className="text-left text-base font-semibold font-heading text-slate-900 dark:text-white hover:no-underline py-5"
            >
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed pb-5">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// ---------- FINAL CTA ----------
const FinalCTA = () => (
  <section className="py-20 md:py-28">
    <div className="max-w-5xl mx-auto px-6">
      <motion.div {...fadeUp} className="relative rounded-3xl bg-slate-900 dark:bg-zinc-900 p-10 sm:p-14 overflow-hidden text-center">
        <div className="absolute inset-0 -z-10 opacity-60" style={{ background: "radial-gradient(ellipse 60% 60% at 50% 0%, rgba(217,70,239,0.35), transparent 70%), radial-gradient(ellipse 60% 60% at 50% 100%, rgba(99,102,241,0.25), transparent 70%)" }} />
        <p className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 mb-6">
          <Flame className="h-3 w-3 text-amber-300" /> Launching Offer • Only ₹{LAUNCH_PRICE}
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
          Your dream college needs <span className="gradient-text">discipline</span>,<br /> not just motivation.
        </h2>
        <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
          Stop wishing. Start preparing. Get your personalised Arrnode Prep dashboard today and build the consistency that boosts your rank.
        </p>
        <div className="mt-4 inline-flex max-w-full flex-wrap items-center justify-center gap-2 text-sm text-white/80">
          <span className="line-through text-white/40">₹{ORIGINAL_PRICE}</span>
          <span className="line-through text-white/40">₹{REGULAR_PRICE}</span>
          <span className="font-bold text-white text-lg">₹{LAUNCH_PRICE}</span>
          <span>•</span>
          <span>Coupon <span className="font-mono font-bold text-amber-300">{COUPON}</span></span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row flex-wrap justify-center gap-3">
          <a href={ACCESS_LINK} target="_blank" rel="noreferrer" data-testid="final-cta-buy-btn" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto rounded-full min-h-12 h-auto px-5 sm:px-7 py-3 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500 hover:from-violet-600 hover:via-fuchsia-600 hover:to-rose-600 text-white font-bold shadow-xl shadow-fuchsia-500/30 hover:scale-[1.02] transition-all whitespace-normal">
              <Rocket className="h-4 w-4 mr-2" /> Get My Personalised Dashboard
            </Button>
          </a>
          <Link to="/login" className="w-full sm:w-auto">
            <Button data-testid="final-cta-login-btn" variant="outline" className="w-full sm:w-auto rounded-full min-h-12 h-auto px-5 sm:px-7 py-3 border-white/20 bg-white/5 text-white hover:bg-white/10 font-medium whitespace-normal">
              <LogIn className="h-4 w-4 mr-2" /> Already paid? Login
            </Button>
          </Link>
        </div>
        <div className="mt-7 flex flex-wrap justify-center gap-5 text-xs text-slate-400">
          {["Instant access", "3 months full access", "Mobile + Desktop"].map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> {b}
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// ---------- FLOATING STICKY BUY PILL ----------
const FloatingBuyPill = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <a
      href={ACCESS_LINK}
      target="_blank"
      rel="noreferrer"
      data-testid="floating-buy-pill"
      className="fixed bottom-5 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 z-40 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 text-white px-4 sm:px-5 py-3 text-sm font-bold shadow-2xl shadow-fuchsia-500/40 hover:scale-105 transition-transform animate-in fade-in slide-in-from-bottom-4 duration-500 sm:w-auto"
    >
      <Rocket className="h-4 w-4" /> Grab ₹{LAUNCH_PRICE} offer
      <ArrowRight className="h-3.5 w-3.5" />
    </a>
  );
};

// ---------- HOME PAGE ----------
export const HomePage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      <PromoBanner />
      <Navbar />
      <Hero />
      <WhoForSection />
      <BuyHook id="after-audience" />
      <ProblemSection />
      <FeaturesSection />
      <BuyHook id="after-features" />
      <PreviewSection />
      <BuySection />
      <TestimonialsSection />
      <HowSection />
      <FAQSection />
      <FinalCTA />
      <Footer />
      <FloatingBuyPill />
    </div>
  );
};
